import { Fragment } from 'react/jsx-runtime';
import { Dot, LoadingSkeleton, useQueryFetch } from 'sonata-react-components';
import { useTheme } from 'styled-components';

import { getAdvertiserVolumetry } from '@/services/advertiser.service';
import { useGlobalStore } from '@/store/global';
import { getSessionByKeys } from '@/store/global/session/selectors';

import './AdvertiserSummary.scss';

import type { MultiFlightAdvertisersState } from 'sonata-static-storage/lib/common/common.types';

type Props = {
  cacheSeed: string;
  filters: Record<string, string | number | (string | number)[]>;
};

type AdvertiserSummaryItem = {
  dot?: {
    color: string;
  };
  icon?: {
    color: string;
    value: string;
  };
  key: keyof MultiFlightAdvertisersState;
  label: string;
  value: number;
};

export function AdvertiserSummary({ cacheSeed, filters }: Readonly<Props>) {
  const { uiColors }: any = useTheme();
  const { user: userId = '' } = useGlobalStore(store => getSessionByKeys(store, ['user']));

  const { data, isLoading, status } = useQueryFetch(
    [userId, filters, cacheSeed, 'advertiser-summary'],
    () => getAdvertiserVolumetry(userId, filters),
    {
      enabled: true
    }
  );

  const items: AdvertiserSummaryItem[] = [
    {
      icon: {
        color: uiColors.advertiserIconColor,
        value: 'fa-solid fa-list-ul'
      },
      key: 'numberOfAdvertisers',
      label: 'Advertisers',
      value: 0
    },
    {
      dot: {
        color: 'var(--color-status-enabled-500, #21CDCD)'
      },
      key: 'numberOfActiveCampaignPlans',
      label: 'Campaign Plans active',
      value: 0
    }
  ];

  if (status === 'success') {
    items.forEach(item => {
      item.value = data?.[item.key] || 0;
    });
  }

  if (isLoading) {
    return <LoadingSkeleton height="100%" />;
  }

  return (
    <div className="advertiserSummary">
      <div className="advertiserSummary__content">
        {items.map((item: AdvertiserSummaryItem, index: number) => (
          <Fragment key={item.label}>
            <div className="advertiserSummary__item">
              {item.icon && <i className={`advertiserSummary__itemIcon fal ${item.icon.value}`} />}
              {item.dot && <Dot className="advertiserSummary__itemDot" color={item.dot.color} label=""></Dot>}
              <span className="advertiserSummary__itemValue">{item.value}</span>
              <span className="advertiserSummary__itemLabel">{item.label}</span>
            </div>
            {index < items.length - 1 && <div className="advertiserSummary__divider" />}
          </Fragment>
        ))}
      </div>
    </div>
  );
}
